/**
 * Authentication Store
 * Zustand store for managing authentication state
 */

import create from 'zustand';
import { storage } from '../utils/storage';
import type { AuthResponse } from '../api/types';
import { useOnboardingStore } from './onboardingStore';
import { usersApi } from '../api/endpoints/users';
import logger from '../utils/logger';

interface User {
  id: number;
  username: string;
  email: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;

  // Actions
  setAuth: (authData: AuthResponse) => Promise<void>;
  setUser: (user: User) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  isLoading: true,

  setAuth: async (authData: AuthResponse) => {
    logger.log('[authStore.setAuth] Starting with token:', authData.token.substring(0, 20) + '...');

    // Store tokens
    await storage.setAccessToken(authData.token);
    if (authData.refresh_token) {
      await storage.setRefreshToken(authData.refresh_token);
    }

    // Store user data
    const user: User = {
      id: authData.user_id,
      username: authData.username,
      email: authData.email,
      phone_number: authData.phone_number,
      first_name: authData.first_name,
      last_name: authData.last_name,
    };
    await storage.setUserData(user);

    logger.log('[authStore.setAuth] Storage complete, updating state to authenticated');

    // IMPORTANT: Reset onboarding store to force fresh fetch for new user
    const { reset: resetOnboarding } = useOnboardingStore.getState();
    resetOnboarding();
    logger.log('[authStore.setAuth] Onboarding store reset');

    // Update state
    set({
      isAuthenticated: true,
      user,
      accessToken: authData.token,
      isLoading: false, // Ensure loading is false after auth
    });

    logger.log('[authStore.setAuth] Complete - isAuthenticated: true, isLoading: false');
  },

  setUser: (user: User) => {
    set({ user });
  },

  logout: async () => {
    await storage.clearAuth();
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
    });
  },

  checkAuth: async () => {
    logger.log('[authStore.checkAuth] Starting...');
    try {
      const token = await storage.getAccessToken();
      const userData = await storage.getUserData();

      logger.log('[authStore.checkAuth] Got from storage:', {
        hasToken: !!token,
        hasUser: !!userData,
        userId: userData?.id
      });

      if (token && userData) {
        // Set token first so API calls work
        set({
          isAuthenticated: true,
          user: userData,
          accessToken: token,
          isLoading: false,
        });

        // Fetch fresh user data from server to get latest first_name/last_name
        try {
          logger.log('[authStore.checkAuth] Fetching fresh user data from server...');
          const freshUserData = await usersApi.getAccount();
          logger.log('[authStore.checkAuth] Fresh user data:', freshUserData);

          const updatedUser: User = {
            id: parseInt(freshUserData.user_id) || userData.id,
            username: freshUserData.username,
            email: freshUserData.email,
            phone_number: freshUserData.phone_number,
            first_name: freshUserData.first_name,
            last_name: freshUserData.last_name,
            gender: freshUserData.gender,
          };

          // Update storage and state with fresh data
          await storage.setUserData(updatedUser);
          set({ user: updatedUser });
          logger.log('[authStore.checkAuth] User data refreshed successfully');
        } catch (refreshError: any) {
          if (refreshError?.response?.status === 401) {
            // Token is invalid server-side — force logout
            logger.warn('[authStore.checkAuth] Token rejected by server (401), logging out');
            await storage.clearAuth();
            set({ isAuthenticated: false, user: null, accessToken: null, isLoading: false });
            return;
          }
          logger.warn('[authStore.checkAuth] Could not refresh user data:', refreshError);
          // Network error or other issue — continue with stored data
        }
      } else {
        logger.log('[authStore.checkAuth] Setting NOT authenticated (no token or user data)');
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          isLoading: false,
        });
      }
    } catch (error) {
      logger.error('[authStore.checkAuth] Error:', error);
      set({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        isLoading: false,
      });
    }
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));

